import React, { useEffect, useState } from 'react';
import { OnboardingStep } from '../../../contexts/OnboardingContext';
import { CheckCircleIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useOnboarding } from '../../../contexts/OnboardingContext';

interface ActionStepProps {
  step: OnboardingStep;
}

export function ActionStep({ step }: ActionStepProps) {
  const { completeStep } = useOnboarding();
  const [isCompleted, setIsCompleted] = useState(false);

  // In a real app, this would listen for the actual action
  useEffect(() => {
    // Example: Listen for connection event
    if (step.requiredAction === 'connect_app') {
      const handleConnection = (event: CustomEvent) => {
        setIsCompleted(true);
        completeStep(step.id);
      };

      window.addEventListener('app_connected' as any, handleConnection);
      return () => {
        window.removeEventListener('app_connected' as any, handleConnection);
      };
    }
  }, [step, completeStep]);

  const getActionContent = () => {
    if (step.requiredAction === 'connect_app') {
      return (
        <>
          <div className="mb-6">
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg p-6 border border-indigo-200 dark:border-indigo-800">
              <h4 className="font-medium text-gray-900 dark:text-white mb-4">
                Let's connect your first app! 🎉
              </h4>
              
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-800">
                      <span className="text-sm font-bold text-indigo-600 dark:text-indigo-300">1</span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Browse through the available apps below
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-800">
                      <span className="text-sm font-bold text-indigo-600 dark:text-indigo-300">2</span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Click on any app you'd like to connect
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-800">
                      <span className="text-sm font-bold text-indigo-600 dark:text-indigo-300">3</span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Follow the OAuth flow to authorize access
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex items-center text-sm text-indigo-600 dark:text-indigo-400">
                <span>Popular choices: Gmail, Slack, GitHub</span>
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </div>
            </div>
          </div>

          {isCompleted && (
            <div className="mb-4 flex items-center justify-center text-green-600 dark:text-green-400">
              <CheckCircleIcon className="h-5 w-5 mr-2" />
              <span className="font-medium">Great job! App connected successfully!</span>
            </div>
          )}
        </>
      );
    }

    return null;
  };

  return (
    <div>
      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
        {step.title}
      </h3>
      
      <p className="text-gray-600 dark:text-gray-300 mb-6">
        {step.description}
      </p>

      {getActionContent()}

      {/* Simulate action completion for demo */}
      {!isCompleted && step.requiredAction === 'connect_app' && (
        <div className="mt-4 text-center">
          <button
            onClick={() => {
              setIsCompleted(true);
              completeStep(step.id);
              // Simulate app connection event
              window.dispatchEvent(new CustomEvent('app_connected'));
            }}
            className="text-sm text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 underline"
          >
            Simulate app connection (for demo)
          </button>
        </div>
      )}
    </div>
  );
}